import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { aiGeneratorService, apiErrorMessage, type CourseJob } from '../../services/aiGeneratorService';
import { emptyMaterial, MaterialInput, type MaterialValue } from './MaterialInput';
import { DIFFICULTIES, LANGUAGES } from './options';
import tokenService from '../../services/tokenService';
import { emptyKnowledgeSelection, KnowledgeSourcePicker, type KnowledgeSelection } from './KnowledgeSourcePicker';

interface CatalogOption {
    id: number;
    name: string;
}

/**
 * Full-course generator form (Tranche B2, #489): topic, audience/goal,
 * target catalog, optional source material. The course is generated
 * asynchronously as an inactive v2 draft and opened in the builder.
 */
export const CourseJobForm: React.FC<{ onCreated: (job: CourseJob) => void; disabled?: boolean }> = ({ onCreated, disabled }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [catalogs, setCatalogs] = useState<CatalogOption[]>([]);
    const [topic, setTopic] = useState('');
    const [goal, setGoal] = useState('');
    const [catalogId, setCatalogId] = useState<number | ''>('');
    const [language, setLanguage] = useState('de');
    const [difficulty, setDifficulty] = useState('beginner');
    const [material, setMaterial] = useState<MaterialValue>(emptyMaterial());
    const [knowledge, setKnowledge] = useState<KnowledgeSelection>(emptyKnowledgeSelection());
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        dataProvider.getList('course_catalogs', { pagination: { page: 1, perPage: 200 }, sort: { field: 'name', order: 'ASC' }, filter: { companyId: tokenService.getCompanyId() } })
            .then(({ data }) => {
                const list = data as CatalogOption[];
                setCatalogs(list);
                if (list.length === 1) setCatalogId(list[0].id);
            })
            .catch(() => setCatalogs([]));
    }, [dataProvider]);

    const canSubmit = !disabled && !submitting && topic.trim() !== '' && goal.trim() !== '' && catalogId !== '';

    const submit = async () => {
        if (catalogId === '') return;
        setSubmitting(true);
        try {
            const job = await aiGeneratorService.createCourseJob({
                topic: topic.trim(),
                goal: goal.trim(),
                catalog_id: catalogId,
                difficulty,
                language,
                source_material: material.text.trim() || undefined,
                use_knowledge_base: knowledge.use,
                knowledge_source_ids: knowledge.use && knowledge.sourceIds.length > 0 ? knowledge.sourceIds : null,
            });
            notify(translate('aiGenerator.course.created'), { type: 'success' });
            onCreated(job);
            setTopic('');
            setGoal('');
            setMaterial(emptyMaterial());
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.course.createError')), { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="oa-aig-card">
            <h2 className="oa-aig-card-title">
                <span className="oa-aig-icon"><SchoolOutlined fontSize="small" /></span>
                {translate('aiGenerator.course.title')}
            </h2>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{translate('aiGenerator.course.intro')}</Typography>
            <div className="oa-aig-form-row">
                <TextField size="small" required label={translate('aiGenerator.course.topic')} value={topic} onChange={(e) => setTopic(e.target.value)} disabled={disabled || submitting} inputProps={{ maxLength: 200, 'aria-label': translate('aiGenerator.course.topic') }} />
                <TextField select size="small" required label={translate('aiGenerator.course.catalog')} value={catalogId} onChange={(e) => setCatalogId(e.target.value === '' ? '' : Number(e.target.value))} disabled={disabled || submitting} inputProps={{ 'aria-label': translate('aiGenerator.course.catalog') }}>
                    {catalogs.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
            </div>
            <TextField fullWidth multiline minRows={2} size="small" required sx={{ mb: 1.5 }} label={translate('aiGenerator.course.goal')} placeholder={translate('aiGenerator.course.goalPlaceholder')} value={goal} onChange={(e) => setGoal(e.target.value)} disabled={disabled || submitting} inputProps={{ 'aria-label': translate('aiGenerator.course.goal') }} />
            <div className="oa-aig-form-row">
                <TextField select size="small" label={translate('aiGenerator.fields.language')} value={language} onChange={(e) => setLanguage(e.target.value)} disabled={disabled || submitting}>
                    {LANGUAGES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label={translate('aiGenerator.fields.difficulty')} value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={disabled || submitting}>
                    {DIFFICULTIES.map((d) => <MenuItem key={d} value={d}>{translate(`aiGenerator.difficulty.${d}`)}</MenuItem>)}
                </TextField>
            </div>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{translate('aiGenerator.course.material')}</Typography>
            <MaterialInput value={material} onChange={setMaterial} minChars={0} disabled={disabled || submitting} />
            <KnowledgeSourcePicker value={knowledge} onChange={setKnowledge} disabled={disabled || submitting} />
            <div className="oa-aig-actions">
                <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlined />}>
                    {translate('aiGenerator.course.submit')}
                </Button>
                {catalogs.length === 0 && <Typography variant="caption" color="text.secondary">{translate('aiGenerator.course.noCatalogs')}</Typography>}
            </div>
        </div>
    );
};

export default CourseJobForm;
